/**
 * HotelBooking.jsx — Hotel booking flow
 *
 * Phases:
 *   details   → shows rooms (from hotel.rooms) + guest form + fare summary
 *   processing → prebook + create_order spinner
 *   polling    → payment done, checking status
 *   done       → confirmation / failure
 *
 * Props:
 *   hotel     – { hotel_code, hotel_name, hotel_rating, rooms[], selected_room,
 *                 check_in (yyyy-mm-dd), check_out (yyyy-mm-dd), price_per_night_inr }
 *   userId    – string
 *   hotelTest – bool
 *   onClose   – () => void
 */

import { useState, useEffect, useRef, useCallback } from "react";
import UserMenu from "./components/UserMenu";

const ORANGE = "#F4560A";
const PURPLE = "#6B3FA0";
const GREEN = "#22C55E";
const RED = "#EF4444";
const AMBER = "#F59E0B";
const GRAY = "#F5F5F7";

const LAMBDA_URL = "https://dw5wuw157e.execute-api.ap-south-1.amazonaws.com";
const TBO_URL = "https://s4nvnzwoc8.execute-api.ap-south-1.amazonaws.com/default/tbo_api_orchesterator/development";

/* Price formatter: 2 decimals only when non-zero, never .00 */
const fmtPrice = (n) => {
  if (!n && n !== 0) return "";
  const num = typeof n === "string" ? parseFloat(n) : n;
  if (isNaN(num)) return "";
  const hasDecimal = (num % 1) !== 0;
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: hasDecimal ? 2 : 0,
    maximumFractionDigits: 2,
  });
};
const MEAL_LABEL = { Room_Only: "Room only", BreakFast: "Breakfast", Breakfast_For_2: "Breakfast for 2", Half_Board: "Half board", Full_Board: "Full board", All_Inclusive: "All inclusive" };
const MEAL_ICON = { Room_Only: "🛏", BreakFast: "🍳", Breakfast_For_2: "🍳", Half_Board: "🍽", Full_Board: "🍱", All_Inclusive: "🎉" };

function fmtDate(s) {
  if (!s) return "";
  try {
    const [y, m, d] = s.split("-");
    return new Date(y, m - 1, d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  } catch { return s; }
}
function nightsBetween(a, b) {
  try { return Math.max(1, Math.round((new Date(b) - new Date(a)) / 86400000)); } catch { return 1; }
}

/* ── Spinner ── */
function Spin({ size = 28, color = ORANGE }) {
  return (
    <svg viewBox="0 0 24 24" style={{ width: size, height: size, animation: "_hs 0.9s linear infinite", display: "block" }}>
      <style>{`@keyframes _hs{to{transform:rotate(360deg)}}`}</style>
      <circle cx="12" cy="12" r="10" fill="none" stroke="#E0E0E0" strokeWidth="3" />
      <path d="M12 2a10 10 0 0 1 10 10" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

/* ── Fullscreen image lightbox — click any gallery image to open ── */
function Lightbox({ images, startIndex, onClose }) {
  const [i, setI] = useState(startIndex);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") setI(p => (p - 1 + images.length) % images.length);
      if (e.key === "ArrowRight") setI(p => (p + 1) % images.length);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [images.length, onClose]);

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 10000, background: "rgba(0,0,0,0.92)",
      display: "flex", alignItems: "center", justifyContent: "center",
    }} onClick={onClose}>
      <button onClick={onClose} style={{
        position: "absolute", top: 20, right: 24, background: "rgba(255,255,255,0.12)",
        border: "none", color: "#fff", width: 44, height: 44, borderRadius: "50%",
        cursor: "pointer", fontSize: 22, display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 2,
      }}>✕</button>

      {images.length > 1 && (
        <button onClick={e => { e.stopPropagation(); setI(p => (p - 1 + images.length) % images.length); }} style={{
          position: "absolute", left: 20, top: "50%", transform: "translateY(-50%)",
          background: "rgba(255,255,255,0.12)", border: "none", color: "#fff",
          width: 48, height: 48, borderRadius: "50%", cursor: "pointer", fontSize: 24,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>‹</button>
      )}

      <img
        src={images[i]} alt=""
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: "90vw", maxHeight: "86vh", objectFit: "contain", borderRadius: 8 }}
      />

      {images.length > 1 && (
        <button onClick={e => { e.stopPropagation(); setI(p => (p + 1) % images.length); }} style={{
          position: "absolute", right: 20, top: "50%", transform: "translateY(-50%)",
          background: "rgba(255,255,255,0.12)", border: "none", color: "#fff",
          width: 48, height: 48, borderRadius: "50%", cursor: "pointer", fontSize: 24,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>›</button>
      )}

      {images.length > 1 && (
        <div style={{ position: "absolute", bottom: 24, left: 0, right: 0, display: "flex", justifyContent: "center", gap: 6 }}>
          {images.map((_, j) => (
            <div key={j} onClick={e => { e.stopPropagation(); setI(j); }} style={{
              width: 8, height: 8, borderRadius: "50%", cursor: "pointer",
              background: j === i ? "#fff" : "rgba(255,255,255,0.35)",
            }} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Image gallery strip ── */
function Gallery({ images, height = 210, rounded = true }) {
  const [i, setI] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [imageLoaded, setImageLoaded] = useState({});
  const [loadError, setLoadError] = useState({});
  const [retryCount, setRetryCount] = useState({}); // Track retries per image
  const loadTimeoutRef = useRef({});
  const radius = rounded ? "20px" : 0;

  // Set 5-second timeout for CURRENT image only
  useEffect(() => {
    if (loadError[i]) return; // Don't timeout if already errored

    loadTimeoutRef.current[i] = setTimeout(() => {
      if (!imageLoaded[i]) {
        setLoadError(prev => ({ ...prev, [i]: true }));
      }
    }, 5000); // 5 second timeout

    return () => {
      if (loadTimeoutRef.current[i]) clearTimeout(loadTimeoutRef.current[i]);
    };
  }, [i, imageLoaded, loadError]);

  const handleImageLoad = () => {
    setImageLoaded(prev => ({ ...prev, [i]: true }));
    setLoadError(prev => ({ ...prev, [i]: false }));
    setRetryCount(prev => ({ ...prev, [i]: 0 }));
    if (loadTimeoutRef.current[i]) clearTimeout(loadTimeoutRef.current[i]);
  };

  const handleImageError = () => {
    setLoadError(prev => ({ ...prev, [i]: true }));
    if (loadTimeoutRef.current[i]) clearTimeout(loadTimeoutRef.current[i]);
  };

  const handleRetry = () => {
    const current = retryCount[i] || 0;
    if (current >= 1) return; // Only 1 retry allowed

    setRetryCount(prev => ({ ...prev, [i]: current + 1 }));
    setImageLoaded(prev => ({ ...prev, [i]: false }));
    setLoadError(prev => ({ ...prev, [i]: false }));
  };

  if (!images?.length) return (
    <div style={{
      width: "100%", height, background: "#F0F0F0", display: "flex",
      alignItems: "center", justifyContent: "center", fontSize: 48,
      borderRadius: radius
    }}>🏨</div>
  );

  return (
    <>
      <div style={{ position: "relative", width: "100%", height, overflow: "hidden", borderRadius: radius, background: "#F0F0F0" }}>
        {/* Loader/Error ONLY for CURRENT image (index i) */}
        {i === i && !imageLoaded[i] && !loadError[i] && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1 }}>
            <Spin size={40} />
          </div>
        )}

        {i === i && loadError[i] && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1, flexDirection: "column", gap: 12, background: "#F5F5F5" }}>
            <div style={{ fontSize: 48 }}>🚫</div>
            <div style={{ fontSize: 14, color: "#666", textAlign: "center" }}>Couldn't load image</div>
            {(retryCount[i] || 0) < 1 && (
              <button onClick={handleRetry} style={{
                padding: "8px 16px", borderRadius: 6, border: "none", background: ORANGE, color: "#fff",
                cursor: "pointer", fontSize: 12, fontWeight: 600
              }}>Retry</button>
            )}
          </div>
        )}

        {/* Image — only render if no error for CURRENT image */}
        {!loadError[i] && (
          <img src={images[i]} alt="" key={`${i}-${retryCount[i] || 0}`} onClick={() => setLightboxOpen(true)}
            onLoad={handleImageLoad}
            style={{ width: "100%", height: "100%", objectFit: "cover", cursor: "zoom-in" }}
            onError={handleImageError} />
        )}

        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top,rgba(0,0,0,0.45) 0%,transparent 55%)", pointerEvents: "none" }} />

        {images.length > 1 && <>
          <button onClick={() => setI(p => (p - 1 + images.length) % images.length)} style={{
            position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)",
            background: "rgba(0,0,0,0.35)", border: "none", color: "#fff",
            borderRadius: "50%", width: 30, height: 30, cursor: "pointer", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center"
          }}>‹</button>
          <button onClick={() => setI(p => (p + 1) % images.length)} style={{
            position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
            background: "rgba(0,0,0,0.35)", border: "none", color: "#fff",
            borderRadius: "50%", width: 30, height: 30, cursor: "pointer", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center"
          }}>›</button>
          <div style={{ position: "absolute", bottom: 8, left: 0, right: 0, display: "flex", justifyContent: "center", gap: 4 }}>
            {images.slice(0, 8).map((_, j) => (
              <div key={j} onClick={() => setI(j)} style={{
                width: 6, height: 6, borderRadius: "50%", cursor: "pointer",
                background: j === i ? "#fff" : "rgba(255,255,255,0.4)"
              }} />
            ))}
          </div>
        </>}
      </div>
      {lightboxOpen && (
        <Lightbox images={images} startIndex={i} onClose={() => setLightboxOpen(false)} />
      )}
    </>
  );
}

/* ── Room card ── */
function RoomCard({ room, selected, onClick, fullPage = false }) {
  const name = Array.isArray(room.Name) ? room.Name.join(", ") : (room.name || "Room");
  const fare = room.TotalFare ?? room.total_fare ?? 0;
  const tax = room.TotalTax ?? room.total_tax ?? 0;
  const meal = room.MealType ?? room.meal_type ?? "Room_Only";
  const ref_ = room.IsRefundable ?? room.is_refundable ?? false;
  const promos = room.RoomPromotion || room.promotions || [];
  const incl = room.Inclusion || room.inclusion || "";
  const deadline = room.LastCancellationDeadline;
  return (
    <div onClick={onClick} style={{
      border: `2px solid ${selected ? ORANGE : "#E8E8E8"}`,
      background: selected ? `${ORANGE}07` : "#FAFAFA",
      borderRadius: 12, padding: fullPage ? "16px 18px" : "12px 14px", cursor: "pointer", transition: "all 0.12s",
    }}>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: fullPage ? 16 : 13, color: "#111", lineHeight: 1.4 }}>{name}</div>
          <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ fontSize: fullPage ? 13 : 11, color: "#555" }}>{MEAL_ICON[meal] || "🛏"} {MEAL_LABEL[meal] || meal}</span>
            <span style={{ fontSize: fullPage ? 12 : 10, fontWeight: 600, color: ref_ ? GREEN : RED }}>{ref_ ? "✓ Free cancel" : "Non-refundable"}</span>
            {promos.map((p, i) => <span key={i} style={{ fontSize: fullPage ? 12 : 10, color: PURPLE, fontWeight: 600 }}>🏷 {p}</span>)}
          </div>
          {incl && <div style={{ fontSize: fullPage ? 12 : 10, color: "#888", marginTop: 4 }}>{incl.split(",").slice(0, 3).join(" · ")}</div>}
          {deadline && ref_ && <div style={{ fontSize: fullPage ? 12 : 10, color: "#aaa", marginTop: 2 }}>Free cancel until {deadline.split(" ")[0]}</div>}
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontWeight: 800, fontSize: fullPage ? 17 : 14, color: selected ? ORANGE : "#111" }}>₹{fmtPrice(fare)}</div>
          <div style={{ fontSize: fullPage ? 12 : 10, color: "#aaa" }}>total</div>
          {tax > 0 && <div style={{ fontSize: fullPage ? 12 : 10, color: "#aaa" }}>+₹{fmtPrice(tax)} tax</div>}
        </div>
      </div>
      {selected && <div style={{ marginTop: 8, fontSize: fullPage ? 13 : 11, color: ORANGE, fontWeight: 700 }}>✓ Selected</div>}
    </div>
  );
}

/* ── Field ── */
function Field({ label, required, children, hint }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <label style={{ fontSize: 13, fontWeight: 600, color: "#555" }}>
        {label}{required && <span style={{ color: RED }}> *</span>}
      </label>
      {children}
      {hint && <div style={{ fontSize: 11, color: "#aaa" }}>{hint}</div>}
    </div>
  );
}

function Input({ value, onChange, placeholder, type = "text", uppercase }) {
  return (
    <input type={type} value={value} placeholder={placeholder}
      onChange={e => onChange(uppercase ? e.target.value.toUpperCase() : e.target.value)}
      style={{
        padding: "11px 13px", borderRadius: 8, border: "1.5px solid #E0E0E0",
        fontFamily: "inherit", fontSize: 14, outline: "none",
        transition: "border 0.1s", boxSizing: "border-box", width: "100%",
      }}
      onFocus={e => e.target.style.borderColor = ORANGE}
      onBlur={e => e.target.style.borderColor = "#E0E0E0"}
    />
  );
}

/* ── Confirmation ── */
function Confirmation({ booking, onClose }) {
  const ok = booking?.status === "BOOKING_CONFIRMED" || booking?.payment_status === "PAID";
  const pnd = booking?.status === "PAYMENT_PENDING" || booking?.payment_status === "PENDING";
  return (
    <div style={{ padding: "32px 20px 40px", textAlign: "center", maxWidth: 460, margin: "0 auto" }}>
      <div style={{
        width: 72, height: 72, borderRadius: "50%", margin: "0 auto 16px",
        fontSize: 34, display: "flex", alignItems: "center", justifyContent: "center",
        background: ok ? `${GREEN}20` : pnd ? `${AMBER}20` : `${RED}20`,
      }}>
        {ok ? "✓" : pnd ? "⏳" : "✗"}
      </div>
      <div style={{ fontWeight: 800, fontSize: 22, color: "#111", marginBottom: 6 }}>
        {ok ? "Booking Confirmed!" : pnd ? "Payment Pending" : "Booking Failed"}
      </div>
      <div style={{ fontSize: 13, color: "#666", marginBottom: 20, lineHeight: 1.6 }}>
        {ok ? `Your stay at ${booking.hotel_name || ""} is confirmed.`
          : pnd ? "Complete your payment to confirm the booking."
            : "Something went wrong. Please try again."}
      </div>
      {(ok || pnd) && (
        <div style={{ background: GRAY, borderRadius: 14, padding: "14px 16px", textAlign: "left", marginBottom: 20 }}>
          {[
            ["Hotel", booking.hotel_name],
            ["Room", booking.room_type_name],
            ["Check-in", fmtDate(booking.check_in_date)],
            ["Check-out", fmtDate(booking.check_out_date)],
            booking.booking_confirmation_no && ["Confirmation #", booking.booking_confirmation_no],
            booking.booking_ref_no && ["Ref No.", booking.booking_ref_no],
            ["Booking ID", booking.rc_booking_id],
            ["Amount", `₹${fmtPrice(booking.amount || 0)}`],
          ].filter(Boolean).map(([k, v]) => (
            <div key={k} style={{
              display: "flex", justifyContent: "space-between",
              padding: "5px 0", borderBottom: "1px solid #EBEBEB", fontSize: 12
            }}>
              <span style={{ color: "#888" }}>{k}</span>
              <span style={{ fontWeight: 600, color: "#111", maxWidth: "58%", textAlign: "right" }}>{v}</span>
            </div>
          ))}
        </div>
      )}
      <button onClick={onClose} style={{
        background: ORANGE, color: "#fff", border: "none",
        borderRadius: 99, padding: "12px 36px", fontFamily: "inherit", fontWeight: 700, fontSize: 14, cursor: "pointer"
      }}>
        Done
      </button>
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   MAIN
══════════════════════════════════════════════════════ */
export default function HotelBooking({ hotel, userId, hotelTest = true, onClose, fullPage = false }) {
  const [phase, setPhase] = useState("details"); // details|processing|polling|done
  const [selRoom, setSelRoom] = useState(null);
  const [images, setImages] = useState([]);
  const [facilities, setFacs] = useState([]);
  const [validation, setVal] = useState(null);   // from prebook ValidationInfo
  const [policies, setPols] = useState([]);     // CancelPolicies
  const [bookingId, setBid] = useState(null);   // rc_booking_id
  const [finalBk, setFinalBk] = useState(null);
  const [statusMsg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [taxOpen_, setTaxOpen_] = useState(false);
  const pollingRef = useRef(null);

  // Guest fields — show all initially, required gated by ValidationInfo after prebook
  const [g, setG] = useState({
    FirstName: "", LastName: "", Email: "", Phoneno: "",
    PAN: "", Passport: "", GST: "",
  });

  const hotelCode = hotel?.hotel_code;
  const checkIn = hotel?.check_in || "";
  const checkOut = hotel?.check_out || "";
  const nights = nightsBetween(checkIn, checkOut);
  const hName = hotel?.hotel_name || "Hotel";

  // Rooms — from hotel.rooms prop, or fetched from /hotels if empty
  const [rooms, setRooms] = useState(hotel?.rooms || []);
  // City/area name recovered from TBO hotel_details — needed for the rooms
  // search on a cold start (direct URL open / share link) where the caller's
  // sessionStorage handoff with hotel_area/city_name is unavailable.
  const [resolvedCityName, setResolvedCityName] = useState(null);
  const [detailsLoaded, setDetailsLoaded] = useState(false);

  // Pre-select room passed from itinerary
  useEffect(() => {
    if (!selRoom && rooms.length) {
      const pre = hotel?.selected_room;
      const match = pre
        ? rooms.find(r => (r.BookingCode || r.booking_code) === (pre.booking_code || pre.BookingCode)) || rooms[0]
        : rooms[0];
      setSelRoom(match);
    }
  }, [rooms]);

  // If rooms not passed, fetch from /hotels endpoint — wait until hotel_details
  // has resolved (or failed) so we have the best chance of a real place_name,
  // since on a cold start (direct URL/share link) hotel.hotel_area etc are unset.
  useEffect(() => {
    if (rooms.length > 0 || !hotelCode || !detailsLoaded) return;
    (async () => {
      try {
        // Convert yyyy-mm-dd → dd-mm-yyyy for API, fallback to today/tomorrow
        const toApiDate = (iso) => {
          if (!iso) {
            const d = new Date(); d.setDate(d.getDate() + 1);
            return [String(d.getDate()).padStart(2, "0"), String(d.getMonth() + 1).padStart(2, "0"), d.getFullYear()].join("-");
          }
          const [y, m, d] = iso.split("-");
          return `${d}-${m}-${y}`;
        };
        const cin = toApiDate(checkIn);
        const cout = checkOut ? toApiDate(checkOut)
          : (() => { const d = new Date(); d.setDate(d.getDate() + 2); return toApiDate(d.toISOString().split("T")[0]); })();

        // Prefer the place name handed off by the caller; fall back to the
        // city/country recovered from TBO hotel_details on a cold start.
        const placeName = hotel?.hotel_area || hotel?.city_name || resolvedCityName || hotel?.hotel_name;
        if (!placeName) {
          setErr("Couldn't determine room availability for this hotel — please go back and reopen it from the trip.");
          return;
        }

        const resp = await fetch(`${LAMBDA_URL}/hotels`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            place_name: placeName,
            check_in: cin, check_out: cout,
            radius_km: 30, user_id: userId,
          }),
        });
        if (!resp.ok) return;
        const data = await resp.json();
        const hotels = data.hotels || [];
        const match = hotels.find(h => h.hotel_code === hotelCode) || hotels[0];
        if (match?.rooms?.length) setRooms(match.rooms);
      } catch { }
    })();
  }, [hotelCode, detailsLoaded, resolvedCityName]);

  // Load hotel details (images, facilities, city) from TBO
  useEffect(() => {
    if (!hotelCode) { setDetailsLoaded(true); return; }
    (async () => {
      try {
        const resp = await fetch(TBO_URL, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ intent: "hotel_details", hotel_code: hotelCode, hotel_test: hotelTest, From: `user:${userId}` }),
        });
        const raw = await resp.json();
        const body = typeof raw.body === "string" ? JSON.parse(raw.body) : raw;
        if (body.status === "error" || body.http_code === 500) {
          console.warn("[HotelBooking] hotel_details returned error:", body.message || body);
          setDetailsLoaded(true);
          return;
        }
        const hd = body.hotel_details || body.data?.HotelDetails?.[0] || {};
        const imgs = [hd.Image, ...(hd.Images || [])].filter(Boolean);
        if (imgs.length === 0) console.warn("[HotelBooking] no images in hotel_details response for", hotelCode, hd);
        setImages(imgs);
        setFacs((hd.HotelFacilities || []).slice(0, 12));
        if (hd.CityName) setResolvedCityName(`${hd.CityName}${hd.CountryName ? ", " + hd.CountryName : ""}`);
        if (body.rc_booking_id) setBid(body.rc_booking_id);
      } catch (e) {
        console.error("[HotelBooking] hotel_details fetch failed:", e);
      } finally {
        setDetailsLoaded(true);
      }
    })();
  }, [hotelCode]);

  const bookingCode = selRoom?.BookingCode || selRoom?.booking_code || "";
  const fareAmt = selRoom ? (selRoom.TotalFare ?? selRoom.total_fare ?? 0) : 0;
  const fareTax = selRoom ? (selRoom.TotalTax ?? selRoom.total_tax ?? 0) : 0;
  const platformFee = 249;
  const gst = Math.round(platformFee * 0.18);
  const total = Math.round(fareAmt + fareTax + platformFee + gst);

  // Determine which fields are required
  const panReq = validation ? (validation.PanMandatory ?? false) : true;  // show until prebook confirms
  const passportReq = validation ? (validation.PassportMandatory ?? false) : false;

  function validate() {
    if (!g.FirstName.trim() || !g.LastName.trim()) return "First and last name are required.";
    if (!g.Email.trim() || !g.Email.includes("@")) return "A valid email is required.";
    if (!g.Phoneno.trim()) return "Phone number is required.";
    if (panReq && !g.PAN.trim()) return "PAN number is required for this booking.";
    if (passportReq && !g.Passport.trim()) return "Passport number is required.";
    return null;
  }

  // ── Prebook → CreateOrder ──────────────────────────────
  async function handlePay() {
    const e = validate();
    if (e) { setErr(e); return; }
    if (!selRoom) { setErr("Please select a room."); return; }
    setErr(""); setBusy(true); setPhase("processing");

    // Step 1: Prebook
    setMsg("Checking room availability…");
    try {
      const preResp = await fetch(TBO_URL, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ intent: "prebook_hotel", hotel_test: hotelTest, From: `user:${userId}`, booking_code: bookingCode }),
      });
      const preRaw = await preResp.json();
      const preBody = typeof preRaw.body === "string" ? JSON.parse(preRaw.body) : preRaw;

      if (preBody.status !== 200 && preBody.status !== "success") throw new Error(preBody.message || "PreBook failed");

      const hr = preBody.data?.HotelResult?.[0] || preBody.HotelResult?.[0];
      const vi = preBody.data?.ValidationInfo || preBody.ValidationInfo || hr?.ValidationInfo;
      if (vi) setVal(vi);
      const cp = hr?.Rooms?.[0]?.CancelPolicies || [];
      if (cp.length) setPols(cp);
      const rid = preBody.rc_booking_id || preBody.data?.rc_booking_id;
      if (rid) setBid(rid);

      // Re-validate after prebook with real ValidationInfo
      if (vi?.PanMandatory && !g.PAN.trim()) {
        setErr("PAN number is required. Please fill it in."); setPhase("details"); setBusy(false); return;
      }
      if (vi?.PassportMandatory && !g.Passport.trim()) {
        setErr("Passport number is required."); setPhase("details"); setBusy(false); return;
      }

      // Step 2: Create order
      setMsg("Creating your order…");
      const roomName = Array.isArray(selRoom.Name) ? selRoom.Name.join(", ") : (selRoom.name || "");
      const guests = [{
        FirstName: g.FirstName, LastName: g.LastName, Email: g.Email, Phoneno: g.Phoneno,
        PaxType: 1, LeadPassenger: true,
        ...(g.PAN ? { PAN: g.PAN.toUpperCase() } : {}),
        ...(g.Passport ? { Passport: g.Passport.toUpperCase() } : {}),
      }];
      const ordResp = await fetch(TBO_URL, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          intent: "create_order_hotel", hotel_test: hotelTest, From: `user:${userId}`,
          rc_booking_id: rid || bookingId,
          booking_code: bookingCode,
          hotel_name: hName, hotel_code: hotelCode,
          room_type_name: roomName,
          check_in_date: checkIn, check_out_date: checkOut,
          guests, currency: "INR",
        }),
      });
      const ordRaw = await ordResp.json();
      const ordBody = typeof ordRaw.body === "string" ? JSON.parse(ordRaw.body) : ordRaw;

      if (ordBody.status !== 200) throw new Error(ordBody.message || "Order creation failed");
      await launchRazorpay(ordBody.data || ordBody, rid || bookingId);
    } catch (ex) {
      setErr(ex.message || "Something went wrong. Please try again.");
      setPhase("details");
    }
    setBusy(false);
  }

  // ── Razorpay ───────────────────────────────────────────
  async function launchRazorpay(orderData, rcBid) {
    const shortUrl = orderData.short_url || orderData.rzp_short_url;
    if (shortUrl) { window.open(shortUrl, "_blank"); setPhase("polling"); startPolling(rcBid); return; }
    const rzpId = orderData.rzp_order_id;
    const rzpKey = orderData.rzp_key_id;
    if (!rzpId || !rzpKey) throw new Error("Payment info missing");
    await new Promise((res, rej) => {
      if (window.Razorpay) return res();
      const s = document.createElement("script"); s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.onload = res; s.onerror = () => rej(new Error("Could not load payment SDK")); document.body.appendChild(s);
    });
    setMsg("Opening payment…");
    const opts = {
      key: rzpKey, amount: Math.round((orderData.amount || 0) * 100), currency: orderData.currency || "INR",
      name: "RouteChef", description: `${hName} — ${fmtDate(checkIn)}`,
      order_id: rzpId,
      prefill: { name: `${g.FirstName} ${g.LastName}`, email: g.Email, contact: g.Phoneno },
      theme: { color: ORANGE },
      handler: () => { setPhase("polling"); startPolling(rcBid); },
      modal: { ondismiss: () => { setPhase("details"); setMsg(""); } },
    };
    new window.Razorpay(opts).open();
  }

  // ── Poll ───────────────────────────────────────────────
  function startPolling(rcBid) {
    setMsg("Confirming your booking — please don't close this page…");
    let tries = 0;
    pollingRef.current = setInterval(async () => {
      tries++;
      try {
        const r = await fetch(TBO_URL, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ intent: "get_status", hotel_test: hotelTest, From: `user:${userId}`, rc_booking_id: rcBid }),
        });
        const raw = await r.json();
        const body = typeof raw.body === "string" ? JSON.parse(raw.body) : raw;
        const bk = body.data?.bookings?.[0] || body.bookings?.[0];
        const st = bk?.status || bk?.payment_status || "";
        if (["BOOKING_CONFIRMED", "PAID", "BOOKING_CONFIRMED"].includes(st) || st.includes("CONFIRM")) {
          clearInterval(pollingRef.current);
          setFinalBk({ ...bk, rc_booking_id: rcBid }); setPhase("done"); return;
        }
        if (["BOOKING_FAILED", "FAILED", "CANCELLED"].includes(st)) {
          clearInterval(pollingRef.current);
          setFinalBk({ ...bk, rc_booking_id: rcBid, status: "BOOKING_FAILED" }); setPhase("done"); return;
        }
      } catch { }
      if (tries >= 20) {
        clearInterval(pollingRef.current);
        setFinalBk({ rc_booking_id: rcBid, status: "PAYMENT_PENDING", hotel_name: hName, check_in_date: checkIn, check_out_date: checkOut });
        setPhase("done");
      }
    }, 3000);
  }
  useEffect(() => () => clearInterval(pollingRef.current), []);

  /* ─── RENDER ─────────────────────────────────────────── */
  const outerStyle = fullPage
    ? { minHeight: "100vh", width: "100%", background: "#fff" }
    : {
      position: "fixed", inset: 0, zIndex: 9000, background: "rgba(0,0,0,0.6)",
      display: "flex", alignItems: "flex-end", justifyContent: "center"
    };

  const cardStyle = fullPage
    ? { background: "#fff", width: "100%", minHeight: "100vh" }
    : {
      background: "#fff", borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 560,
      maxHeight: "94vh", overflowY: "auto", boxShadow: "0 -8px 40px rgba(0,0,0,0.25)"
    };

  return (
    <div style={outerStyle} onClick={fullPage ? undefined : (e => { if (e.target === e.currentTarget) onClose(); })}>
      <div style={cardStyle}>
        {/* Drag handle — only meaningful for the bottom-sheet modal */}
        {!fullPage && (
          <div style={{ display: "flex", justifyContent: "center", padding: "10px 0 2px" }}>
            <div style={{ width: 40, height: 4, borderRadius: 2, background: "#D0D0D0" }} />
          </div>
        )}
        {/* Header */}
        <div style={fullPage
          ? {
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "24px 32px 12px", maxWidth: 880, margin: "0 auto", width: "100%", boxSizing: "border-box"
          }
          : { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 16px 8px" }}>
          {fullPage ? (
            <div style={{ flex: 0, flexShrink: 0 }}>
            </div>
          ) : (
            <div style={{ fontWeight: 800, fontSize: 15, color: "#111" }}>
              {phase === "done" ? "Booking Summary" : phase === "polling" ? "Confirming Booking" : "Book Hotel"}
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginLeft: "auto", flexShrink: 0 }}>

            <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: fullPage ? 24 : 20, color: "#999", padding: 4, flexShrink: 0 }}>✕</button>
          </div>
        </div>

        {/* ── PROCESSING ── */}
        {phase === "processing" && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "64px 24px", gap: 20, textAlign: "center" }}>
            <Spin size={48} />
            <div style={{ fontWeight: 800, fontSize: 20, color: "#111" }}>Processing</div>
            <div style={{ fontSize: 13, color: "#666", lineHeight: 1.6 }}>{statusMsg || "Please wait…"}</div>
            <div style={{ background: "#FFF7F0", border: `1px solid ${ORANGE}40`, borderRadius: 12, padding: "12px 18px", maxWidth: 340 }}>
              <span style={{ fontWeight: 700, color: ORANGE }}>Important: </span>
              <span style={{ fontSize: 12, color: "#555" }}>Don't refresh or press Back while your transaction processes.</span>
            </div>
          </div>
        )}

        {/* ── POLLING ── */}
        {phase === "polling" && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "64px 24px", gap: 20, textAlign: "center" }}>
            <Spin size={48} />
            <div style={{ fontWeight: 800, fontSize: 20, color: "#111" }}>Confirming your booking</div>
            <div style={{ fontSize: 13, color: "#666" }}>{statusMsg}</div>
          </div>
        )}

        {/* ── DONE ── */}
        {phase === "done" && <Confirmation booking={finalBk} onClose={onClose} />}

        {/* ── DETAILS (main booking form) ── */}
        {phase === "details" && (
          <div><div style={{ paddingLeft: 40, paddingRight: 40, paddingTop: "20px", paddingBottom: "24px" }}>
            <Gallery images={images} height={400} />
          </div>


            <div style={fullPage
              ? { padding: "24px 32px 0", maxWidth: 880, margin: "0 auto", width: "100%", boxSizing: "border-box" }
              : { padding: "14px 16px 0" }}>
              {/* Hotel name */}
              <div style={{ fontWeight: 800, fontSize: fullPage ? 26 : 18, color: "#111" }}>{hName}</div>
              <div style={{ fontSize: fullPage ? 14 : 12, color: "#888", marginTop: 2 }}>
                {hotel?.city_name || hotel?.hotel_area || ""}
                {hotel?.hotel_rating ? ` · ${"★".repeat(Math.min(hotel.hotel_rating, 5))}` : ""}
              </div>

              {/* Dates strip */}
              {checkIn && (
                <div style={{ display: "flex", gap: 0, marginTop: 12, background: GRAY, borderRadius: 10 }}>
                  {[
                    ["CHECK-IN", fmtDate(checkIn), `From ${(hotel?.checkin_time || "12:00").slice(0, 5)}`],
                    ["CHECK-OUT", fmtDate(checkOut), `By ${(hotel?.checkout_time || "11:00").slice(0, 5)}`],
                    ["NIGHTS", `${nights} night${nights !== 1 ? "s" : ""}`, ""],
                  ].map(([lbl, val, sub], i, arr) => (
                    <div key={lbl} style={{
                      flex: 1, padding: "10px 12px",
                      borderRight: i < arr.length - 1 ? "1px solid #E0E0E0" : "none"
                    }}>
                      <div style={{ fontSize: 9, color: "#aaa", fontWeight: 700, letterSpacing: 0.6 }}>{lbl}</div>
                      <div style={{ fontWeight: 700, fontSize: 12, color: "#111", marginTop: 2 }}>{val}</div>
                      {sub && <div style={{ fontSize: 10, color: "#aaa", marginTop: 1 }}>{sub}</div>}
                    </div>
                  ))}
                </div>
              )}

              {/* Facilities */}
              {facilities.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div style={{
                    fontSize: 10, color: "#aaa", fontWeight: 700, letterSpacing: 0.6,
                    textTransform: "uppercase", marginBottom: 6
                  }}>Facilities</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                    {facilities.map((f, i) => (
                      <span key={i} style={{
                        fontSize: 10, background: GRAY,
                        borderRadius: 99, padding: "3px 9px", color: "#555"
                      }}>{f}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Room selection ── */}
              <div style={{ marginTop: 18 }}>
                <div style={{ fontWeight: 700, fontSize: fullPage ? 18 : 14, color: "#111", marginBottom: 10 }}>Select Room</div>
                {rooms.length === 0 ? (
                  <div style={{ fontSize: fullPage ? 15 : 13, color: "#aaa", padding: "16px 0", textAlign: "center" }}>
                    Room information not available for these dates.
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {rooms.map((r, i) => (
                      <RoomCard key={r.BookingCode || r.booking_code || i} room={r}
                        selected={(r.BookingCode || r.booking_code) === (selRoom?.BookingCode || selRoom?.booking_code)}
                        onClick={() => setSelRoom(r)} fullPage={fullPage} />
                    ))}
                  </div>
                )}
              </div>

              {/* ── Guest details ── */}
              <div style={{ marginTop: 24 }}>
                <div style={{ fontWeight: 700, fontSize: fullPage ? 18 : 14, color: "#111", marginBottom: 5 }}>Guest Details</div>
                <div style={{ fontSize: fullPage ? 13 : 11, color: "#888", marginBottom: 14 }}>
                  {validation
                    ? "Fill all marked fields to complete your booking."
                    : "All fields shown — required fields confirmed after availability check."}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                  <Field label="First Name" required><Input value={g.FirstName} onChange={v => setG(p => ({ ...p, FirstName: v }))} placeholder="John" /></Field>
                  <Field label="Last Name" required><Input value={g.LastName} onChange={v => setG(p => ({ ...p, LastName: v }))} placeholder="Doe" /></Field>
                  <Field label="Email" required><Input type="email" value={g.Email} onChange={v => setG(p => ({ ...p, Email: v }))} placeholder="john@example.com" /></Field>
                  <Field label="Phone" required hint="+91XXXXXXXXXX"><Input type="tel" value={g.Phoneno} onChange={v => setG(p => ({ ...p, Phoneno: v }))} placeholder="+918XXXXXXXXX" /></Field>
                  {/* PAN — shown always, required if PanMandatory */}
                  <Field label="PAN Number" required={panReq} hint={!panReq ? "Optional" : "Required for Indian nationals"}>
                    <Input value={g.PAN} onChange={v => setG(p => ({ ...p, PAN: v }))} placeholder="ABCDE1234F" uppercase />
                  </Field>
                  {/* Passport — shown only if PassportMandatory confirmed */}
                  {(passportReq || validation?.PassportMandatory) && (
                    <Field label="Passport No." required={passportReq}>
                      <Input value={g.Passport} onChange={v => setG(p => ({ ...p, Passport: v }))} placeholder="A1234567" uppercase />
                    </Field>
                  )}
                </div>
              </div>

              {/* Cancellation policy (shown if returned from prebook) */}
              {policies.length > 0 && (
                <div style={{
                  marginTop: 14, background: "#FFFBF0", border: `1px solid ${AMBER}50`,
                  borderRadius: 10, padding: "10px 12px"
                }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#92400E", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.4 }}>
                    Cancellation Policy
                  </div>
                  {policies.map((p, i) => (
                    <div key={i} style={{ fontSize: 11, color: "#666", marginBottom: 3 }}>
                      From {p.FromDate?.split(" ")[0]}: {
                        p.CancellationCharge === 0 ? "Free cancellation"
                          : p.ChargeType === "Percentage" ? `${p.CancellationCharge}% of total`
                            : `₹${Math.round(p.CancellationCharge)} charge`
                      }
                    </div>
                  ))}
                </div>
              )}

              {/* Fare breakdown */}
              {selRoom && (
                <div style={{ marginTop: 16, background: GRAY, borderRadius: 12, padding: fullPage ? "16px 18px" : "12px 14px" }}>
                  <div style={{ fontWeight: 700, fontSize: fullPage ? 16 : 13, color: "#111", marginBottom: 10 }}>Fare Breakdown</div>
                  {/* Room charge = TotalFare (base, no tax) */}
                  <div style={{
                    display: "flex", justifyContent: "space-between",
                    padding: "6px 0", fontSize: fullPage ? 14 : 12, borderBottom: "1px solid #E8E8E8"
                  }}>
                    <span style={{ color: "#666" }}>Room charge</span>
                    <span style={{ color: "#111" }}>₹{fmtPrice(fareAmt)}</span>
                  </div>
                  {/* Taxes & fees — expandable */}
                  <div style={{ borderBottom: "1px solid #E8E8E8" }}>
                    <div onClick={() => setTaxOpen_(!taxOpen_)}
                      style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        padding: "6px 0", cursor: "pointer"
                      }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <span style={{ fontSize: fullPage ? 14 : 12, color: "#666" }}>Taxes & fees</span>
                        <span style={{ fontSize: fullPage ? 11 : 10, color: "#bbb" }}>{taxOpen_ ? "▲" : "▼"}</span>
                      </div>
                      <span style={{ fontSize: fullPage ? 14 : 12, color: "#111" }}>₹{fmtPrice(fareTax + platformFee + gst)}</span>
                    </div>
                    {taxOpen_ && (
                      <div style={{ paddingLeft: 14, paddingBottom: 8, display: "flex", flexDirection: "column", gap: 5 }}>
                        {fareTax > 0 && (
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: fullPage ? 13 : 11, color: "#666" }}>
                            <span>Hotel tax</span><span>₹{fmtPrice(fareTax)}</span>
                          </div>
                        )}
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: fullPage ? 13 : 11, color: "#666" }}>
                          <span>Platform fee</span><span>₹{platformFee}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: fullPage ? 13 : 11, color: "#666" }}>
                          <span>GST on platform fee (18%)</span><span>₹{gst}</span>
                        </div>
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0 0", fontWeight: 800, fontSize: fullPage ? 17 : 14 }}>
                    <span>Total</span><span style={{ color: ORANGE }}>₹{fmtPrice(total)}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Sticky CTA — full-width band, button content centered to reading width */}
            <div style={{
              position: "sticky", bottom: 0, background: "#fff",
              borderTop: "1px solid #F0F0F0", marginTop: 16,
              padding: fullPage ? "16px 32px 24px" : "14px 16px 20px",
            }}>
              <div style={fullPage ? { maxWidth: 880, margin: "0 auto" } : undefined}>
                {err && (
                  <div style={{
                    fontSize: 12, color: RED, marginBottom: 8, textAlign: "center",
                    background: `${RED}10`, borderRadius: 8, padding: "6px 10px"
                  }}>{err}</div>
                )}
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <button onClick={handlePay} disabled={!selRoom || busy} style={{
                    width: "50%", padding: "10px 20px", borderRadius: 8, border: "none",
                    background: selRoom ? ORANGE : "#E0E0E0", color: "#fff",
                    fontFamily: "inherit", fontWeight: 600, fontSize: 18,
                    cursor: selRoom && !busy ? "pointer" : "not-allowed", opacity: busy ? 0.7 : 1,
                  }}>
                    {busy ? <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}><Spin size={18} color="#fff" /> Processing…</span>
                      : selRoom ? `Pay ₹${fmtPrice(total)}` : "Select a room"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
